import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useOutletContext } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import './Enrollments.css';

const StudyRegistrations = () => {
  const { t } = useTranslation();
  const { data, getCourseById } = useData();
  const { selectedLocation } = useOutletContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudy, setSelectedStudy] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [expandedKey, setExpandedKey] = useState(null);

  const studies = useMemo(() => data?.studies || [], [data?.studies]);
  const courses = useMemo(() => data?.courses || [], [data?.courses]);

  const toId = (value) => String(value?.id || value?._id || value || '');

  const getCourseStudyYearId = useCallback((course) => {
    return toId(course?.studyYear?.id || course?.studyYear?._id || course?.studyYear || course?.studyYearId);
  }, []);

  const studyYearLookup = useMemo(() => {
    const lookup = new Map();
    studies.forEach((study) => {
      (study.studyYears || []).forEach((year) => {
        const yearId = toId(year.id || year._id);
        if (!yearId) return;
        lookup.set(yearId, { study, year });
      });
    });
    return lookup;
  }, [studies]);

  const moduleToYearLookup = useMemo(() => {
    const lookup = new Map();
    studies.forEach((study) => {
      (study.studyYears || []).forEach((year) => {
        const yearId = toId(year.id || year._id);
        if (!yearId) return;
        (year.modules || []).forEach((mod) => {
          const moduleId = toId(mod.id || mod._id);
          if (moduleId) lookup.set(moduleId, { study, year });
        });
      });
    });
    return lookup;
  }, [studies]);

  const getCohortDisplayName = (cohort) => {
    const name = String(cohort?.name || '').trim();
    const code = String(cohort?.code || '').trim();
    if (!name) return code;
    if (!code) return name;
    if (name.toLowerCase().includes(code.toLowerCase())) return name;
    return `${name} (${code})`;
  };

  const getStudyOptionLabel = (study) => {
    const cohortNames = (study.cohorts || []).map(getCohortDisplayName).filter(Boolean);
    return cohortNames.length > 0 ? `${study.name} ${cohortNames.join(', ')}` : study.name;
  };

  const courseMetadata = useMemo(() => {
    const entries = [];

    courses.forEach((course) => {
      const directYearId = getCourseStudyYearId(course);
      let linkedYear = studyYearLookup.get(directYearId);
      if (!linkedYear) {
        const mids = [
          toId(course?.module?.id || course?.module?._id || course?.module),
          ...(course?.modules || []).map((m) => toId(m?.id || m?._id || m))
        ].filter(Boolean);
        for (const mid of mids) {
          const entry = moduleToYearLookup.get(mid);
          if (entry) { linkedYear = entry; break; }
        }
      }
      if (!linkedYear) return;

      const { study, year } = linkedYear;
      const yearId = toId(year.id || year._id);
      const linkedModules = Array.isArray(course.modules) && course.modules.length > 0
        ? course.modules
        : (course.module ? [course.module] : []);

      if (linkedModules.length === 0) {
        entries.push({
          id: String(course.id || course._id),
          studyId: String(study.id || study._id),
          studyName: study.name,
          studyLocation: study.location,
          yearId,
          yearLabel: year.name || `Studiejaar ${year.yearNumber || year.year}`,
          yearNumber: year.yearNumber || year.year,
          moduleId: `course-${String(course.id || course._id)}-default`,
          moduleName: 'Module nog niet ingesteld',
          courseName: course.name,
          courseCode: course.code,
          courseLocation: course.location
        });
        return;
      }

      linkedModules.forEach((moduleDoc) => {
        const moduleId = String(moduleDoc?.id || moduleDoc?._id || moduleDoc || '');
        entries.push({
          id: String(course.id || course._id),
          studyId: String(study.id || study._id),
          studyName: study.name,
          studyLocation: study.location,
          yearId,
          yearLabel: year.name || `Studiejaar ${year.yearNumber || year.year}`,
          yearNumber: year.yearNumber || year.year,
          moduleId,
          moduleName: moduleDoc?.name || moduleDoc?.code || 'Module',
          courseName: course.name,
          courseCode: course.code,
          courseLocation: course.location
        });
      });
    });

    return entries;
  }, [courses, getCourseStudyYearId, studyYearLookup, moduleToYearLookup]);

  const courseMetadataById = useMemo(() => {
    const mapping = new Map();
    const ordered = [...courseMetadata].sort((a, b) => Number(a.yearNumber || 0) - Number(b.yearNumber || 0));
    ordered.forEach((entry) => {
      if (!mapping.has(entry.id)) {
        mapping.set(entry.id, entry);
      }
    });
    return mapping;
  }, [courseMetadata]);

  const studiesWithAssignments = useMemo(() => {
    const studyIds = new Set(courseMetadata.map((entry) => String(entry.studyId)));
    return studies.filter((study) => studyIds.has(String(study.id || study._id)));
  }, [courseMetadata, studies]);

  const groupedRegistrations = useMemo(() => {
    const registrations = new Map();

    data.enrollments.forEach((enrollment) => {
      const course = getCourseById(enrollment.courseId);
      const student = data.students.find((item) => String(item.id) === String(enrollment.studentId));
      const metadata = courseMetadataById.get(String(enrollment.courseId));
      const courseYearId = getCourseStudyYearId(course);
      let linkedYear = studyYearLookup.get(courseYearId);
      if (!linkedYear) {
        const mids = [
          toId(course?.module?.id || course?.module?._id || course?.module),
          ...(course?.modules || []).map((m) => toId(m?.id || m?._id || m))
        ].filter(Boolean);
        for (const mid of mids) {
          const e = moduleToYearLookup.get(mid);
          if (e) { linkedYear = e; break; }
        }
      }
      const fallbackStudyId = String(enrollment.studyId || linkedYear?.study?.id || linkedYear?.study?._id || '');
      const fallbackStudy = studies.find((item) => String(item.id || item._id) === fallbackStudyId);

      if (!student) {
        return;
      }

      const resolvedStudyId = metadata?.studyId || fallbackStudyId;
      const resolvedStudyName = metadata?.studyName || fallbackStudy?.name || linkedYear?.study?.name || 'Onbekende studie';
      const resolvedYearId = metadata?.yearId || courseYearId || String(linkedYear?.year?.id || linkedYear?.year?._id || '');
      const resolvedYearLabel = metadata?.yearLabel || linkedYear?.year?.name || `Studiejaar ${Number(enrollment.currentStudyYear || linkedYear?.year?.yearNumber || 1)}`;
      const resolvedModuleId = metadata?.moduleId || `course-${String(course?.id || course?._id || enrollment.courseId)}-fallback`;
      const resolvedModuleName = metadata?.moduleName || 'Module nog niet ingesteld';
      const resolvedCourseName = metadata?.courseName || course?.name || 'Onbekend vak';
      const resolvedCourseCode = metadata?.courseCode || course?.code || '';
      const resolvedCourseLocation = metadata?.courseLocation || course?.location || student.location || '';

      if (!resolvedStudyId) {
        return;
      }

      const normalizedLocation = String(selectedLocation || 'all').toLowerCase();
      const enrollmentLocation = String(resolvedCourseLocation || '').toLowerCase();
      if (normalizedLocation !== 'all' && enrollmentLocation !== normalizedLocation) {
        return;
      }

      if (selectedStudy && resolvedStudyId !== selectedStudy) {
        return;
      }

      if (selectedYear && resolvedYearId !== selectedYear) {
        return;
      }

      if (selectedStatus && enrollment.status !== selectedStatus) {
        return;
      }

      const haystack = [
        student.name,
        student.email,
        resolvedStudyName,
        resolvedYearLabel,
        resolvedModuleName,
        resolvedCourseName,
        resolvedCourseCode
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      if (!haystack.includes(searchTerm.trim().toLowerCase())) {
        return;
      }

      const key = `${resolvedStudyId}:${student.id}`;
      if (!registrations.has(key)) {
        registrations.set(key, {
          key,
          studyId: resolvedStudyId,
          studyName: resolvedStudyName,
          studentId: student.id,
          student,
          years: new Map(),
          modules: new Map(),
          courses: new Map(),
          activeCount: 0,
          completedCount: 0,
          cancelledCount: 0,
          latestEnrollmentDate: null,
          rows: []
        });
      }

      const registration = registrations.get(key);
      registration.years.set(resolvedYearId, resolvedYearLabel);
      registration.modules.set(resolvedModuleId, resolvedModuleName);
      registration.courses.set(String(course?.id || course?._id || enrollment.courseId), resolvedCourseName);
      registration.rows.push({
        enrollmentId: String(enrollment.id || enrollment._id),
        courseCode: resolvedCourseCode,
        courseName: resolvedCourseName,
        moduleName: resolvedModuleName,
        yearLabel: resolvedYearLabel,
        status: enrollment.status || 'active',
        enrollmentDate: enrollment.enrollmentDate ? new Date(enrollment.enrollmentDate) : null
      });

      if (enrollment.status === 'active') registration.activeCount += 1;
      if (enrollment.status === 'completed') registration.completedCount += 1;
      if (enrollment.status === 'cancelled') registration.cancelledCount += 1;

      const enrollmentDate = enrollment.enrollmentDate ? new Date(enrollment.enrollmentDate) : null;
      if (enrollmentDate && !Number.isNaN(enrollmentDate.getTime())) {
        if (!registration.latestEnrollmentDate || enrollmentDate > registration.latestEnrollmentDate) {
          registration.latestEnrollmentDate = enrollmentDate;
        }
      }
    });

    const groupedByStudy = new Map();
    Array.from(registrations.values())
      .sort((left, right) => left.student.name.localeCompare(right.student.name, 'nl'))
      .forEach((registration) => {
        if (!groupedByStudy.has(registration.studyId)) {
          groupedByStudy.set(registration.studyId, {
            studyId: registration.studyId,
            studyName: registration.studyName,
            registrations: []
          });
        }

        groupedByStudy.get(registration.studyId).registrations.push(registration);
      });

    return Array.from(groupedByStudy.values()).sort((left, right) => left.studyName.localeCompare(right.studyName, 'nl'));
  }, [courseMetadataById, data.enrollments, data.students, getCourseById, getCourseStudyYearId, searchTerm, selectedLocation, selectedStatus, selectedStudy, selectedYear, studies, studyYearLookup, moduleToYearLookup]);

  const availableYears = selectedStudy
    ? courseMetadata.filter((entry) => entry.studyId === selectedStudy)
    : courseMetadata;

  const totalRegistrations = groupedRegistrations.reduce((count, group) => count + group.registrations.length, 0);
  const totalStudents = new Set(
    groupedRegistrations.flatMap((group) => group.registrations.map((registration) => registration.studentId))
  ).size;
  const totalAssignments = groupedRegistrations.reduce(
    (count, group) => count + group.registrations.reduce((sum, registration) => sum + registration.courses.size, 0),
    0
  );

  return (
    <div className="enrollments-page">
      <div className="page-header">
        <div>
          <h1>{t('admin.studyRegistrations')}</h1>
          <p className="page-subtitle">{t('admin.studyRegistrationNote')}</p>
        </div>
      </div>

      <div className="registration-stat-grid">
        <div className="registration-stat-card">
          <span className="registration-stat-value">{groupedRegistrations.length}</span>
          <span className="registration-stat-label">Studies</span>
        </div>
        <div className="registration-stat-card">
          <span className="registration-stat-value">{totalStudents}</span>
          <span className="registration-stat-label">Studenten</span>
        </div>
        <div className="registration-stat-card">
          <span className="registration-stat-value">{totalRegistrations}</span>
          <span className="registration-stat-label">Studie-inschrijvingen</span>
        </div>
        <div className="registration-stat-card">
          <span className="registration-stat-value">{totalAssignments}</span>
          <span className="registration-stat-label">Actieve vakkoppelingen</span>
        </div>
      </div>

      <div className="enrollment-filter-toolbar">
        <input
          type="search"
          className="enrollment-search-input"
          placeholder="Zoek op student, studie, module of vak"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select className="enrollment-select-input" value={selectedStudy} onChange={(e) => {
          setSelectedStudy(e.target.value);
          setSelectedYear('');
        }}>
          <option value="">Alle studies</option>
          {studiesWithAssignments.map((study) => {
            const studyId = toId(study.id || study._id);
            return (
              <option key={studyId} value={studyId}>{getStudyOptionLabel(study)}</option>
            );
          })}
        </select>
        <select className="enrollment-select-input" value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
          <option value="">Alle studiejaren</option>
          {Array.from(new Map(availableYears.map((entry) => [entry.yearId, entry]))).values().map((entry) => (
            <option key={entry.yearId} value={entry.yearId}>{entry.yearLabel}</option>
          ))}
        </select>
        <select className="enrollment-select-input" value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
          <option value="">Alle statussen</option>
          <option value="active">Actief</option>
          <option value="completed">Afgerond</option>
          <option value="cancelled">Geannuleerd</option>
        </select>
      </div>

      <div className="registration-groups">
        {groupedRegistrations.map((studyGroup) => (
          <section key={studyGroup.studyId} className="assignment-section">
            <div className="assignment-section-header">
              <h2>{studyGroup.studyName}</h2>
              <span>{studyGroup.registrations.length} studenten</span>
            </div>

            <div className="registration-grid">
              {studyGroup.registrations.map((registration) => (
                <article key={registration.key} className="registration-card">
                  <div className="registration-card-header">
                    <div>
                      <h3>{registration.student.name}</h3>
                      <p>{registration.student.email}</p>
                    </div>
                    <span className="registration-card-chip">{registration.student.location || 'Onbekende locatie'}</span>
                  </div>

                  <div className="registration-card-meta">
                    <div>
                      <span className="registration-meta-label">Studiejaren</span>
                      <strong>{Array.from(registration.years.values()).join(', ') || '-'}</strong>
                    </div>
                    <div>
                      <span className="registration-meta-label">Modules</span>
                      <strong>{registration.modules.size}</strong>
                    </div>
                    <div>
                      <span className="registration-meta-label">Vakken</span>
                      <strong>{registration.courses.size}</strong>
                    </div>
                  </div>

                  <div className="registration-status-row">
                    {registration.activeCount > 0 && <span className="status-badge active">Actief {registration.activeCount}</span>}
                    {registration.completedCount > 0 && <span className="status-badge completed">Afgerond {registration.completedCount}</span>}
                    {registration.cancelledCount > 0 && <span className="status-badge cancelled">Geannuleerd {registration.cancelledCount}</span>}
                  </div>

                  <div className="registration-course-list">
                    {Array.from(registration.courses.values()).slice(0, 4).map((courseName) => (
                      <span key={courseName} className="registration-course-chip">{courseName}</span>
                    ))}
                    {registration.courses.size > 4 && (
                      <span className="registration-course-chip muted">+{registration.courses.size - 4} meer</span>
                    )}
                  </div>

                  <div className="registration-card-footer">
                    <p className="registration-last-updated">
                      Laatste koppeling: {registration.latestEnrollmentDate ? registration.latestEnrollmentDate.toLocaleDateString('nl-NL') : '-'}
                    </p>
                    <button
                      type="button"
                      className="btn-expand-registrations"
                      onClick={() => setExpandedKey(expandedKey === registration.key ? null : registration.key)}
                    >
                      {expandedKey === registration.key ? '▲ Verberg details' : '▼ Bekijk inschrijvingen'}
                    </button>
                  </div>

                  {expandedKey === registration.key && (
                    <div className="registration-detail-panel">
                      <div className="registration-detail-header">
                        <span className="registration-detail-title">Inschrijvingsdetails</span>
                        <Link to="/admin/enrollments" className="registration-detail-link">Bewerken in Vaktoewijzingen →</Link>
                      </div>
                      <table className="registration-detail-table">
                        <thead>
                          <tr>
                            <th>Vak</th>
                            <th>Module</th>
                            <th>Studiejaar</th>
                            <th>Status</th>
                            <th>Ingeschreven op</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...registration.rows]
                            .sort((a, b) => a.courseName.localeCompare(b.courseName, 'nl'))
                            .map((row) => (
                              <tr key={row.enrollmentId}>
                                <td><strong>{row.courseCode}</strong>{row.courseCode ? ' — ' : ''}{row.courseName}</td>
                                <td>{row.moduleName}</td>
                                <td>{row.yearLabel}</td>
                                <td>
                                  <span className={`status-badge ${row.status === 'completed' ? 'completed' : row.status === 'cancelled' ? 'cancelled' : 'active'}`}>
                                    {row.status === 'completed' ? 'Afgerond' : row.status === 'cancelled' ? 'Geannuleerd' : 'Actief'}
                                  </span>
                                </td>
                                <td>{row.enrollmentDate ? row.enrollmentDate.toLocaleDateString('nl-NL') : '-'}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </article>
              ))}
            </div>
          </section>
        ))}

        {groupedRegistrations.length === 0 && (
          <p className="no-data">{t('common.noData')}</p>
        )}
      </div>
    </div>
  );
};

export default StudyRegistrations;